import React, { useEffect } from 'react';
import { PAGE_SIZE_OPTIONS } from '@constants/pagination';
import { Table, Form, Button, Tooltip, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import SaveCancelTooltip from '../save-cancel-tooltip/SaveCancelTooltip';
import EditableCell from '../editable-cell/EditableCell';
import { useRemoteDataTable } from './useRemoteDataTable';

const RemoteDataTable = ({
  useData,
  rowKey,
  isEditable = false,
  columnStructures = {},
  defaultColumnStructure = {
    editable: isEditable,
  },
  columnsToIgnore = [] as string[],
  expandableRowRender = undefined,
  expandableCondition = undefined,
  onShowDetail = undefined,
  params = null,
}: {
  useData?: any;
  rowKey: string;
  isEditable?: boolean;
  columnStructures?: any;
  defaultColumnStructure?: { editable: any };
  columnsToIgnore?: string[];
  expandableRowRender?: (r: any) => React.ReactNode;
  expandableCondition?: (r: any) => boolean;
  onShowDetail?: (r: any) => void;
  params?: any;
}) => {
  const {
    data,
    totalCount,
    loading,
    updating,
    error,
    form,
    currentPage,
    pageSize,
    onEdit,
    onCancelEdit,
    isRowBeingEdited,
    onSave,
    onChangePage,
  } = useRemoteDataTable(useData, rowKey);

  useEffect(() => {
    if (error) {
      message.error(error.message);
    }
  }, [error]);

  const visibleColumns = data[0]
    ? Object.keys(data[0]).filter((col) => !columnsToIgnore.includes(col))
    : [];

  const columns =
    visibleColumns.length > 0
      ? visibleColumns.map((col, index) => {
          const baseCol = {
            title: col,
            dataIndex: col,
            key: col[rowKey],
            inputType: typeof data[0][col] === 'number' ? 'number' : 'text',
            ...defaultColumnStructure,
            ...columnStructures[col],
          };
          // Make the first column a clickable link
          if (index === 0 && onShowDetail) {
            return {
              ...baseCol,
              render: (text, record) => (
                <Button
                  type="link"
                  onClick={() => onShowDetail(record)}
                  style={{ padding: 0 }}
                >
                  {text}
                </Button>
              ),
            };
          }

          return baseCol;
        })
      : [];

  if (isEditable) {
    columns.push({
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        isRowBeingEdited(record) ? (
          <SaveCancelTooltip onSave={() => onSave(record)} onCancel={onCancelEdit} />
        ) : (
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => onEdit(record)}
              type="link" // Optional: makes it icon-only, no border
            />
          </Tooltip>
        ),
    });
  }

  const editStateColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editing: isRowBeingEdited(record),
        dataIndex: col.dataIndex,
        title: col.title,
        inputType: col.inputType,
        options: col.options,
        trueValue: col.trueValue,
        falseValue: col.falseValue,
      }),
    };
  });

  return (
    <Form form={form} component={false}>
      <Table
        scroll={{ x: 'max-content' }} // forces horizontal scroll, table takes min-width = sum of columns
        loading={loading || updating}
        dataSource={data}
        rowKey={rowKey}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onChange: onChangePage,
        }} // use separate Pagination component
        columns={editStateColumns}
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        expandable={
          expandableRowRender && expandableCondition
            ? {
                expandedRowRender: expandableRowRender,
                rowExpandable: expandableCondition,
              }
            : undefined
        }
      />
    </Form>
  );
};

export default RemoteDataTable;
